import React, { useState, useEffect, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────────────
 *  CubeCodeScene
 *  Isometric 3D cube tumbling along a road with floating
 *  code snippets & a volumetric aura at max speed.
 *
 *  Sequence: starts slow (3.0 s/tumble, no code, no aura).
 *  After 2 s the speed ramps down to 0.05 s over ~3 s with
 *  an ease-in-out curve. Code snippets fade in at speed < 2.5,
 *  flame/aura ignites at speed < 0.8.
 *
 *  All CSS classes prefixed "ccs-" to avoid collisions.
 * ───────────────────────────────────────────────────── */

const INITIAL_SPEED = 3.0;   // slow start
const TARGET_SPEED  = 0.05;  // max velocity
const RAMP_DELAY    = 2000;  // ms before ramp begins
const RAMP_DURATION = 5000;  // ms to reach max speed
const CODE_THRESHOLD  = 2.5; // snippets start appearing below this
const FLAME_THRESHOLD = 0.8; // aura ignites below this

/* ── Scoped styles (speed-independent) ─────────────── */
const STATIC_STYLES = `
  /* ── Container ── */
  .ccs-root {
    position: absolute;
    inset: 0;
    perspective: 1200px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(circle at center, #1a1a24 0%, #1e1e20 80%);
    overflow: hidden;
    contain: layout style paint;
  }

  /* Push the scene down on mobile so it clears the text overlay */
  @media (max-width: 767px) {
    .ccs-root {
      align-items: flex-end;
      padding-bottom: 12vh;
    }
  }

  /* ── Camera ── */
  .ccs-camera {
    position: relative;
    transform-style: preserve-3d;
    transform: rotateX(54deg) rotateZ(45deg);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* ── Road slab ── */
  .ccs-road-slab {
    position: absolute;
    width: 2400px;
    height: 400px;
    transform-style: preserve-3d;
  }

  .ccs-road-surface {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(22,22,29,0.8) 0%, rgba(32,32,41,0.8) 50%, rgba(22,22,29,0.8) 100%);
    transform-style: preserve-3d;
    mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%);
    overflow: hidden;
    backdrop-filter: blur(4px);
  }

  .ccs-wall-front {
    position: absolute;
    width: 100%;
    height: var(--ccs-road-depth);
    background: #08080a;
    bottom: 0;
    left: 0;
    transform-origin: bottom;
    transform: rotateX(-90deg) translateY(var(--ccs-road-depth));
    border-top: 1px solid rgba(255,255,255,0.05);
  }

  .ccs-wall-side {
    position: absolute;
    width: var(--ccs-road-depth);
    height: 100%;
    background: #050507;
    top: 0;
    right: 0;
    transform-origin: right;
    transform: rotateY(90deg) translateX(var(--ccs-road-depth));
  }

  /* ── Road stripes ── */
  .ccs-stripes {
    position: absolute;
    top: 50%;
    left: -400px;
    transform: translateY(-50%);
    width: 3200px;
    height: 4px;
    background: repeating-linear-gradient(
      90deg,
      rgba(255,255,255,0.9) 0px,
      rgba(255,255,255,0.9) 60px,
      transparent 60px,
      transparent 160px
    );
    animation: ccs-moveStripes var(--ccs-speed) linear infinite;
    z-index: 5;
  }

  @keyframes ccs-moveStripes {
    0%   { transform: translateY(-50%) translateX(-160px); }
    100% { transform: translateY(-50%) translateX(0); }
  }

  /* ── Code flow ── */
  .ccs-code-flow {
    position: absolute;
    inset: 0;
    transform-style: preserve-3d;
    pointer-events: none;
  }

  .ccs-code-track {
    position: absolute;
    display: flex;
    align-items: center;
    width: 8000px;
    height: 100%;
    animation: ccs-moveCode var(--ccs-code-speed) linear infinite;
  }

  @keyframes ccs-moveCode {
    0%   { transform: translateX(calc(-1 * var(--ccs-track-length))); }
    100% { transform: translateX(0); }
  }

  .ccs-snippet {
    position: absolute;
    background: rgba(10, 10, 15, 0.7);
    padding: 12px 18px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.05);
    font-family: 'Fira Code', 'Courier New', monospace;
    font-size: 11px;
    line-height: 1.4;
    color: #9cdcfe;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    white-space: nowrap;
    backdrop-filter: blur(12px);
    transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .ccs-kw { color: #c586c0; }
  .ccs-fn { color: #dcdcaa; }
  .ccs-st { color: #ce9178; }
  .ccs-vr { color: #9cdcfe; }
  .ccs-cm { color: #6a9955; font-style: italic; }

  /* ── Cube actor ── */
  .ccs-cube-group {
    position: absolute;
    width: var(--ccs-size);
    height: var(--ccs-size);
    transform-style: preserve-3d;
    animation: ccs-slideCompensate var(--ccs-speed) linear infinite;
  }

  .ccs-cube-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transform-origin: bottom left;
    animation: ccs-tumble var(--ccs-speed) linear infinite;
  }

  .ccs-face {
    position: absolute;
    width: var(--ccs-size);
    height: var(--ccs-size);
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.1);
    backface-visibility: visible;
  }

  .ccs-face-top    { transform: translateZ(var(--ccs-size)); background: #ffffff; }
  .ccs-face-bottom { transform: translateZ(0);               background: #999; }
  .ccs-face-front  { transform: rotateX(-90deg); transform-origin: bottom; background: #eeeeee; }
  .ccs-face-back   { transform: rotateX(90deg);  transform-origin: top;    background: #cccccc; }
  .ccs-face-left   { transform: rotateY(-90deg); transform-origin: left;   background: #dddddd; }
  .ccs-face-right  { transform: rotateY(90deg);  transform-origin: right;  background: #dddddd; }

  .ccs-reflection {
    opacity: 0.4;
    filter: blur(4px);
    background: linear-gradient(to top, rgba(255,255,255,0.8), rgba(255,255,255,0.1)) !important;
    mask-image: linear-gradient(to bottom, rgba(0,0,0,1), transparent);
    -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1), transparent);
  }

  @keyframes ccs-tumble {
    0%   { transform: rotateY(0deg); }
    100% { transform: rotateY(-90deg); }
  }

  @keyframes ccs-slideCompensate {
    0%   { transform: translateX(0); }
    100% { transform: translateX(var(--ccs-size)); }
  }

  /* ── Shadow ── */
  .ccs-shadow {
    position: absolute;
    width: var(--ccs-size);
    height: var(--ccs-size);
    background: rgba(0, 0, 0, 0.5);
    filter: blur(10px);
    transform: translateZ(1px);
    animation: ccs-shadowMove var(--ccs-speed) linear infinite;
  }

  @keyframes ccs-shadowMove {
    0%   { transform: translateZ(1px) translateX(0) scale(1); opacity: 0.5; }
    50%  { transform: translateZ(1px) translateX(calc(var(--ccs-size) * -0.5)) scale(0.7, 1.1); opacity: 0.2; }
    100% { transform: translateZ(1px) translateX(calc(var(--ccs-size) * -1)) scale(1); opacity: 0.5; }
  }

  /* ── 3D Volumetric Aura ── */
  .ccs-aura-container {
    position: absolute;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    pointer-events: none;
    transition: opacity 0.4s ease;
  }

  .ccs-aura-shell {
    position: absolute;
    inset: -20px;
    border-radius: 50%;
    border: 2px solid var(--ccs-aura-color);
    background: radial-gradient(circle, var(--ccs-aura-inner) 0%, transparent 70%);
    filter: blur(10px);
    mix-blend-mode: screen;
    animation: ccs-auraPulse 0.1s infinite alternate;
  }

  .ccs-shell-xy { transform: translateZ(calc(var(--ccs-size) / 2)); }
  .ccs-shell-yz { transform: translateZ(calc(var(--ccs-size) / 2)) rotateY(90deg); }
  .ccs-shell-xz { transform: translateZ(calc(var(--ccs-size) / 2)) rotateX(90deg); }

  @keyframes ccs-auraPulse {
    0%   { transform: translateZ(calc(var(--ccs-size) / 2)) scale(var(--ccs-flame-scale)); opacity: 0.7; }
    100% { transform: translateZ(calc(var(--ccs-size) / 2)) scale(calc(var(--ccs-flame-scale) * 1.1)); opacity: 1; }
  }

  .ccs-plasma-glow {
    position: absolute;
    inset: -30%;
    background: radial-gradient(circle, var(--ccs-aura-color) 0%, transparent 80%);
    filter: blur(25px);
    transform: translateZ(calc(var(--ccs-size) / 2));
    opacity: 0.6;
  }

  .ccs-trail {
    position: absolute;
    width: 400px;
    height: var(--ccs-size);
    left: 50%;
    top: 0;
    transform-origin: left center;
    background: linear-gradient(90deg, var(--ccs-aura-color) 0%, transparent 100%);
    filter: blur(15px);
    transform: translateZ(calc(var(--ccs-size) / 2)) scaleX(var(--ccs-trail-scale));
    animation: ccs-trailWaver 0.15s infinite;
  }

  @keyframes ccs-trailWaver {
    0%   { opacity: 0.4; transform: translateZ(calc(var(--ccs-size) / 2)) scaleY(1); }
    100% { opacity: 0.7; transform: translateZ(calc(var(--ccs-size) / 2)) scaleY(1.2); }
  }

  /* ── Bottom vignette ── */
  .ccs-vignette {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%);
  }
`;

/* ── Code snippets ─────────────────────────────────── */
const snippets = [
  <><div><span className="ccs-kw">const</span> <span className="ccs-fn">rotate</span> = (<span className="ccs-vr">cube</span>) =&gt; &#123;</div><div>  <span className="ccs-vr">cube</span>.<span className="ccs-vr">rotation</span>.<span className="ccs-vr">y</span> -= <span className="ccs-vr">Math</span>.<span className="ccs-vr">PI</span> / <span className="ccs-vr">2</span>;</div><div>&#125;</div></>,
  <><div><span className="ccs-kw">export default function</span> <span className="ccs-fn">Engine</span>() &#123;</div><div>  <span className="ccs-kw">const</span> [<span className="ccs-vr">state</span>, <span className="ccs-fn">set</span>] = <span className="ccs-fn">useState</span>(<span className="ccs-st">'stable'</span>);</div><div>  <span className="ccs-kw">return</span> &lt;<span className="ccs-fn">Immersive</span> /&gt;;</div><div>&#125;</div></>,
  <><div><span className="ccs-cm">{"// Synchronizing physics clock"}</span></div><div><span className="ccs-kw">function</span> <span className="ccs-fn">animate</span>(<span className="ccs-vr">time</span>) &#123;</div><div>  <span className="ccs-vr">requestAnimationFrame</span>(<span className="ccs-fn">animate</span>);</div><div>  <span className="ccs-fn">updatePhysics</span>(<span className="ccs-vr">time</span>);</div><div>&#125;</div></>,
  <><div><span className="ccs-kw">if</span> (<span className="ccs-vr">collision</span>.<span className="ccs-fn">detected</span>()) &#123;</div><div>  <span className="ccs-vr">audio</span>.<span className="ccs-fn">play</span>(<span className="ccs-st">'thud.wav'</span>);</div><div>  <span className="ccs-vr">particleSystem</span>.<span className="ccs-fn">emit</span>(<span className="ccs-vr">20</span>);</div><div>&#125;</div></>,
  <><div><span className="ccs-cm">{"/* Render volumetric road */"}</span></div><div>&lt;<span className="ccs-fn">Road</span> <span className="ccs-vr">depth</span>=&#123;<span className="ccs-vr">40</span>&#125; <span className="ccs-vr">texture</span>=<span className="ccs-st">"grid"</span> /&gt;</div></>,
  <><div><span className="ccs-kw">const</span> <span className="ccs-fn">matrix</span> = <span className="ccs-vr">data</span>.<span className="ccs-fn">map</span>(<span className="ccs-vr">row</span> =&gt; <span className="ccs-vr">row</span>.<span className="ccs-fn">process</span>());</div></>,
  <><div><span className="ccs-kw">yield</span> <span className="ccs-fn">put</span>(&#123; <span className="ccs-vr">type</span>: <span className="ccs-st">'STREAM_SYNC'</span>, <span className="ccs-vr">payload</span>: <span className="ccs-vr">id</span> &#125;);</div></>,
  <><div><span className="ccs-kw">await</span> <span className="ccs-vr">physics</span>.<span className="ccs-fn">compute</span>(&#123; <span className="ccs-vr">dt</span>: <span className="ccs-vr">1</span>/<span className="ccs-vr">60</span> &#125;);</div></>,
  <><div><span className="ccs-kw">return</span> <span className="ccs-vr">view</span>.<span className="ccs-fn">render</span>(<span className="ccs-vr">state</span>.<span className="ccs-fn">snapshot</span>());</div></>,
  <><div><span className="ccs-cm">{"// Logic end of stream segment"}</span></div><div><span className="ccs-vr">process</span>.<span className="ccs-fn">exit</span>(<span className="ccs-vr">0</span>);</div></>,
];

const laneSequence = [
  { side: "left", offset: 0 },  { side: "right", offset: 0 },
  { side: "left", offset: 4 },  { side: "right", offset: -4 },
  { side: "right", offset: 2 }, { side: "left", offset: -2 },
  { side: "left", offset: 5 },  { side: "right", offset: -5 },
  { side: "left", offset: -3 }, { side: "right", offset: 3 },
];

/* ── CodeTrack (speed-aware visibility) ────────────── */
const CodeTrack = ({ speed }) => {
  const intensity = Math.max(0, (CODE_THRESHOLD - speed) / (CODE_THRESHOLD - TARGET_SPEED));
  const visibleCount = intensity * snippets.length;
  const trackItems = [...snippets, ...snippets];

  return (
    <div className="ccs-code-flow">
      <div className="ccs-code-track">
        {trackItems.map((code, i) => {
          const lane = laneSequence[i % laneSequence.length];
          const leftPos = i * 400;
          const lateralPos =
            lane.side === "left"
              ? `calc(18% + ${lane.offset}px)`
              : `calc(82% + ${lane.offset}px)`;
          const isVisible = (i % snippets.length) < visibleCount;

          return (
            <div
              key={i}
              className="ccs-snippet"
              style={{
                left: `${leftPos}px`,
                top: lateralPos,
                transform: "translateY(-50%) translateZ(1px)",
                opacity: isVisible ? 1 : 0,
                pointerEvents: isVisible ? "auto" : "none",
              }}
            >
              {code}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── VolumetricAura ────────────────────────────────── */
const VolumetricAura = ({ flameOpacity }) => (
  <div className="ccs-aura-container" style={{ opacity: flameOpacity }}>
    <div className="ccs-plasma-glow" />
    <div className="ccs-aura-shell ccs-shell-xy" />
    <div className="ccs-aura-shell ccs-shell-yz" />
    <div className="ccs-aura-shell ccs-shell-xz" />
    <div className="ccs-trail" />
  </div>
);

/* ── Cube ──────────────────────────────────────────── */
const Cube = ({ isReflection = false }) => (
  <div className="ccs-cube-wrapper">
    <div className={`ccs-face ccs-face-top    ${isReflection ? "ccs-reflection" : ""}`} />
    <div className={`ccs-face ccs-face-bottom ${isReflection ? "ccs-reflection" : ""}`} />
    <div className={`ccs-face ccs-face-front  ${isReflection ? "ccs-reflection" : ""}`} />
    <div className={`ccs-face ccs-face-back   ${isReflection ? "ccs-reflection" : ""}`} />
    <div className={`ccs-face ccs-face-left   ${isReflection ? "ccs-reflection" : ""}`} />
    <div className={`ccs-face ccs-face-right  ${isReflection ? "ccs-reflection" : ""}`} />
  </div>
);

/* ── Ease-out helper — fast start, lingers in the
 *  progressive code range, then settles into max speed ── */
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/* ── useSpeedRamp hook ─────────────────────────────── */
function useSpeedRamp() {
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const rafRef = useRef(null);

  const ramp = useCallback(() => {
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / RAMP_DURATION, 1);
      const eased = easeOutCubic(t);
      const current = INITIAL_SPEED + (TARGET_SPEED - INITIAL_SPEED) * eased;
      setSpeed(current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const timer = setTimeout(ramp, RAMP_DELAY);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ramp]);

  return speed;
}

/* ── Main scene ────────────────────────────────────── */
const CubeCodeScene = React.memo(function CubeCodeScene() {
  const speed = useSpeedRamp();

  // Flame/aura intensity — ignites below FLAME_THRESHOLD
  const flameIntensity = speed <= FLAME_THRESHOLD
    ? Math.min(1, (FLAME_THRESHOLD - speed) / (FLAME_THRESHOLD - TARGET_SPEED))
    : 0;
  const flameScale = 0.9 + flameIntensity * 0.4;
  const trailScale = 1 + flameIntensity * 2;

  // Dynamic CSS custom properties driven by current speed
  const rootVars = {
    "--ccs-speed": `${speed}s`,
    "--ccs-size": "80px",
    "--ccs-road-depth": "40px",
    "--ccs-stripe-cycle": "160px",
    "--ccs-track-length": "4000px",
    "--ccs-code-speed": `calc(${speed}s * (4000 / 160))`,
    "--ccs-aura-color": "#ff3c00",
    "--ccs-aura-inner": "#ffcc00",
    "--ccs-flame-scale": flameScale,
    "--ccs-trail-scale": trailScale,
  };

  return (
    <div className="ccs-root" style={rootVars}>
      <style>{STATIC_STYLES}</style>

      {/* Isometric camera */}
      <div className="ccs-camera">
        {/* Road */}
        <div className="ccs-road-slab">
          <div className="ccs-wall-front" />
          <div className="ccs-wall-side" />
          <div className="ccs-road-surface">
            <CodeTrack speed={speed} />
            <div className="ccs-stripes" />
          </div>
        </div>

        {/* Cube actor */}
        <div className="ccs-cube-group">
          <div className="ccs-shadow" />

          {/* Volumetric Aura */}
          <VolumetricAura flameOpacity={flameIntensity} />

          {/* Reflection */}
          <div
            style={{
              transform: "scaleZ(-1)",
              transformStyle: "preserve-3d",
              position: "absolute",
              pointerEvents: "none",
            }}
          >
            <Cube isReflection />
          </div>

          <Cube />
        </div>
      </div>

      <div className="ccs-vignette" />
    </div>
  );
});

export default CubeCodeScene;
